import { InputfieldTypes } from "../../../../../../enums/InputFieldAttributesEnums";
import InputField from "../../../../../../reusable-components/TNInputField";
import styles from "./EditTruckForm.module.scss";
import { dropDownFields, getEditJobFields } from "./constants";
import { useEffect, useState } from "react";
import { Select } from "antd";
import { useFormContext } from "react-hook-form";
import { allowedKeys } from "../../../../../../constants/keyboardKeys";
import { formatNumber } from "../../../../../../utils/numberFormatterUtil";
import { stripCurrencyFormatting } from "../../../../../../Utilities/regex";

const EditTruckForm = ({ defaultValues, setEditedValues, setSelectedRow }) => {
  const { watch, setValue, getValues } = useFormContext();
  const materialCost = watch("materialCost");
  const materialPrice = watch("materialPrice");
  const truckingCost = watch("truckingCost");
  const truckingPrice = watch("truckingPrice");
  const minimumHours = watch("minimumHours");

  const { Option } = Select;
  const { TON_TXT, LOAD_TXT, HOUR_TXT } = dropDownFields;

  const [selectedTruckingUnit, setSelectedTruckingUnit] = useState(
    defaultValues?.truckingCostType ?? TON_TXT
  );
  const handleTruckingUnitChange = (value: string) => {
    setSelectedTruckingUnit(value);
    setSelectedTruckingPriceUnit(value);
    if (value !== TON_TXT) setValue("minimumHours", undefined);
  };
  const selectTruckingAfterFieldProps = (
    <Select
      value={selectedTruckingUnit}
      onChange={handleTruckingUnitChange}
      className={styles.selectSection}
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedTruckingPriceUnit, setSelectedTruckingPriceUnit] = useState(
    defaultValues?.truckingPriceType ?? TON_TXT
  );
  const handleTruckingPriceUnitChange = (value: string) => {
    setSelectedTruckingUnit(value);
    setSelectedTruckingPriceUnit(value);
  };
  const selectTruckingPriceAfterFieldProps = (
    <Select
      value={selectedTruckingPriceUnit}
      onChange={handleTruckingPriceUnitChange}
      className={styles.selectSection}
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedLandedPriceUnit, setSelectedLandedPriceUnit] = useState(
    defaultValues?.landedPriceType ?? TON_TXT
  );
  const handleLandedPriceUnitChange = (value: string) => {
    setSelectedLandedPriceUnit(value);
  };
  const selectLandedPriceAfterFieldProps = (
    <Select
      value={selectedLandedPriceUnit}
      onChange={handleLandedPriceUnitChange}
      className={styles.selectSection}
      disabled
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const handleTruckingPriceCost = (isTruckPrice: boolean, markup: number) => {
    const [truckingVal, fieldName, adjacentFieldName] = isTruckPrice
      ? [
          stripCurrencyFormatting(truckingPrice),
          "truckingCost",
          "truckingPrice",
        ]
      : [
          stripCurrencyFormatting(truckingCost),
          "truckingPrice",
          "truckingCost",
        ];
    if (truckingVal !== undefined && truckingVal !== null) {
      const val = (truckingVal * markup)?.toFixed(2);
      setValue(fieldName, !isNaN(val) ? formatNumber(val) : "");
      setValue(
        adjacentFieldName,
        formatNumber(Number(truckingVal)?.toFixed(2))
      );
    }
  };

  const onNumericBlur = (fieldName) => (e) => {
    if (e.target.value) {
      const stripped = stripCurrencyFormatting(e.target.value);
      const value = formatNumber(Number(stripped).toFixed(2));
      if (!isNaN(Number(stripped))) {
        setValue(fieldName, formatNumber(Number(stripped).toFixed(2)));
      } else {
        setValue(fieldName, null);
      }
      clearErrors(fieldName);
    }
  };

  const onKeyDownHandler = (e) => {
    // Allow control and navigation keys
    if (
      allowedKeys?.includes(e?.key) ||
      e?.ctrlKey ||
      e?.altKey ||
      e?.metaKey
    ) {
      return;
    }

    // Allow digitls
    if (/^\d$/.test(e?.key)) {
      return;
    }

    // Allow decimal point if enabled
    if (e?.key === ".") {
      const currentValue = e?.target?.value?.toString() || "";
      if (currentValue.includes(".")) {
        e?.preventDefault();
        return;
      }
      return;
    }

    e?.preventDefault();
  };

  const editJobFields = getEditJobFields({
    selectTruckingAfterFieldProps,
    selectTruckingPriceAfterFieldProps,
    selectLandedPriceAfterFieldProps,
    handleTruckingPriceCost,
    selectedTruckingPriceUnit,
    onKeyDownHandler,
    onNumericBlur,
  });

  useEffect(() => {
    setEditedValues({
      truckingCost: Number(stripCurrencyFormatting(truckingCost)).toFixed(2),
      truckingPrice: Number(stripCurrencyFormatting(truckingPrice)).toFixed(2),
      truckingCostType: selectedTruckingUnit,
      truckingPriceType: selectedTruckingPriceUnit,
      minimumHours: minimumHours ?? undefined,
    });
    setSelectedRow(defaultValues);
  }, [
    truckingCost,
    truckingPrice,
    selectedTruckingUnit,
    selectedTruckingPriceUnit,
    minimumHours,
  ]);
  return (
    <div className={styles.EditMaterialFormContainer}>
      {editJobFields.map((field) => (
        <div className={styles.EditMaterialInputContainer}>
          <InputField
            name={field.name}
            type={field.type}
            label={field.label}
            disabled={field.disabled}
            addonAfter={field.addonLater}
            placeholder={field.placeholder}
            numberPrecision={field.numberPrecision}
            onKeyDown={field.onKeyDownHandler}
            onBlur={field.onBlur}
            prefix={field.prefix}
          />
        </div>
      ))}
    </div>
  );
};

export default EditTruckForm;
